import { ArrowLeft, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CreateDeviceRequest } from '../../api/devices';
import type { Device } from '../../types';
import { DeviceTypeSelector } from './DeviceTypeSelector';
import { deviceTypes, type DeviceTypeKey } from './deviceTypes';

interface AddDeviceDialogProps {
  open: boolean;
  /** Pre-selected type (opened from a category "+") or null to show the selector. */
  initialType?: DeviceTypeKey | null;
  recorders: Device[];
  onClose: () => void;
  onSubmit: (request: CreateDeviceRequest) => Promise<void>;
}

export function AddDeviceDialog({ open, initialType = null, recorders, onClose, onSubmit }: AddDeviceDialogProps) {
  const [selectedType, setSelectedType] = useState<DeviceTypeKey | null>(initialType);

  useEffect(() => {
    if (!open) return;
    setSelectedType(initialType);
    document.body.classList.add('modal-open');
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, open, initialType]);

  if (!open) return null;

  const meta = selectedType ? deviceTypes[selectedType] : null;
  const Form = meta?.form;
  const Icon = meta?.icon ?? Plus;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="camera-dialog" role="dialog" aria-modal="true" aria-labelledby="add-device-title">
        <header className="camera-dialog__header">
          {meta ? (
            <>
              <button
                className="icon-button icon-button--small"
                type="button"
                onClick={() => (initialType ? onClose() : setSelectedType(null))}
                aria-label="Назад к выбору типа"
              >
                <ArrowLeft size={18} />
              </button>
              <span className="camera-dialog__icon"><Icon size={21} aria-hidden="true" /></span>
              <div>
                <span className="eyebrow">Новое устройство</span>
                <h2 id="add-device-title">{meta.key === 'camera' ? 'Добавить камеру' : `Добавить ${meta.shortTitle.toLowerCase()}`}</h2>
              </div>
            </>
          ) : (
            <>
              <span className="camera-dialog__icon"><Plus size={21} aria-hidden="true" /></span>
              <div>
                <span className="eyebrow">Устройства</span>
                <h2 id="add-device-title">Добавить устройство</h2>
              </div>
            </>
          )}
          <button className="icon-button icon-button--small" type="button" onClick={onClose} aria-label="Закрыть"><X size={18} /></button>
        </header>

        {meta && Form ? (
          <Form recorders={recorders} onSubmit={onSubmit} onCancel={onClose} />
        ) : (
          <div className="camera-form">
            <DeviceTypeSelector
              onSelect={(key) => setSelectedType(key as DeviceTypeKey)}
              onCancel={onClose}
            />
          </div>
        )}
      </section>
    </div>
  );
}
